import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/lib/supabase';
import { seriesBadgeClass } from '@/lib/accountSeries';
import {
  Search, Loader2, Building2, CheckSquare, Square, X, Users, Plus, Keyboard,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────

interface AccountItem {
  account_id: string;
  account_name: string | null;
  billed_status: string | null;
  source: 'crm' | 'jobs' | 'manual';
}

interface AccountFilterPickerProps {
  /** Which series to show accounts for (e.g. ['5','6']) — empty = all */
  seriesFilter: string[];
  /** Currently selected account IDs */
  selectedAccountIds: string[];
  /** Called when selection changes */
  onSelectionChange: (ids: string[]) => void;
  /** Max height for the account list */
  maxHeight?: string;
  /** Compact mode for sidebar */
  compact?: boolean;
  /** Optional label override */
  label?: string;
}

// ─── Series color helper ────────────────────────────────────────────
const seriesColor = (s: string) => seriesBadgeClass(s);

// ─── Component ──────────────────────────────────────────────────────

const AccountFilterPicker: React.FC<AccountFilterPickerProps> = ({
  seriesFilter,
  selectedAccountIds,
  onSelectionChange,
  maxHeight = 'max-h-[220px]',
  compact = false,
  label,
}) => {
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Track which series we've already loaded to avoid re-fetching
  const [loadedSeries, setLoadedSeries] = useState<string>('');

  const selectedSet = useMemo(() => new Set(selectedAccountIds), [selectedAccountIds]);

  // ─── Load Accounts via RPC — ALL accounts for selected series ───
  // Paginated to bypass PostgREST's default 1000-row response limit.
  const loadAccounts = useCallback(async () => {
    const seriesKey = seriesFilter.length > 0 ? seriesFilter.sort().join(',') : 'all';
    // Skip if we already loaded this exact series set
    if (seriesKey === loadedSeries && accounts.length > 0) return;

    setLoading(true);
    setLoadError(null);

    const PAGE_SIZE = 1000;
    const MAX_PAGES = 50; // safety cap => 50k rows max

    try {
      // Use the RPC function that queries both CRM and taxicaller_data
      // Paginate with .range() until we get fewer than PAGE_SIZE rows
      const allRows: any[] = [];
      let page = 0;
      let lastBatchSize = PAGE_SIZE;

      while (lastBatchSize === PAGE_SIZE && page < MAX_PAGES) {
        const from = page * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;
        const { data, error } = await supabase
          .rpc('get_distinct_accounts_by_series', {
            series_prefixes: seriesFilter.length > 0 ? seriesFilter : null,
          })
          .range(from, to);

        if (error) throw error;

        const batch = data || [];
        lastBatchSize = batch.length;
        allRows.push(...batch);
        page += 1;

        if (lastBatchSize < PAGE_SIZE) break;
      }

      if (allRows.length >= MAX_PAGES * PAGE_SIZE) {
        console.warn(
          `[AccountFilterPicker] Account list hit safety cap of ${MAX_PAGES * PAGE_SIZE} rows — some accounts may be missing. Increase MAX_PAGES if needed.`
        );
      }
      if (allRows.length > 10000) {
        console.warn(
          `[AccountFilterPicker] Loaded ${allRows.length} accounts (>10,000). Consider narrowing series filter for performance.`
        );
      }

      const items: AccountItem[] = allRows.map((row: any) => ({
        account_id: row.account_id,
        account_name: row.account_name || null,
        billed_status: row.billed_status || null,
        source: (row.source === 'jobs' ? 'jobs' : 'crm') as 'crm' | 'jobs',
      }));

      setAccounts(items);
      setLoadedSeries(seriesKey);

      if (items.length === 0) {
        setLoadError('No accounts found for the selected series');
      }
    } catch (err: any) {
      console.error('Failed to load accounts via RPC:', err);
      setLoadError(`Failed to load accounts: ${err.message}`);
      // Fallback: paginated direct CRM query
      try {
        const allRows: any[] = [];
        let page = 0;
        let lastBatchSize = PAGE_SIZE;

        while (lastBatchSize === PAGE_SIZE && page < MAX_PAGES) {
          const from = page * PAGE_SIZE;
          const to = from + PAGE_SIZE - 1;

          let query = supabase
            .from('crm_accounts')
            .select('account_id, account_name, billed_status')
            .order('account_id')
            .range(from, to);

          if (seriesFilter.length > 0) {
            const likeFilters = seriesFilter.map(s => `account_id.like.${s}%`).join(',');
            query = query.or(likeFilters);
          }

          const { data: crmData, error: crmError } = await query;
          if (crmError) throw crmError;

          const batch = crmData || [];
          lastBatchSize = batch.length;
          allRows.push(...batch);
          page += 1;

          if (lastBatchSize < PAGE_SIZE) break;
        }

        if (allRows.length > 10000) {
          console.warn(
            `[AccountFilterPicker fallback] Loaded ${allRows.length} accounts (>10,000).`
          );
        }

        const items: AccountItem[] = allRows.map((row: any) => ({
          account_id: row.account_id,
          account_name: row.account_name || null,
          billed_status: row.billed_status || null,
          source: 'crm' as const,
        }));

        setAccounts(items);
        setLoadedSeries(seriesKey);
        setLoadError(items.length === 0 ? 'No accounts found' : null);
      } catch (fallbackErr: any) {
        console.error('Fallback CRM query also failed:', fallbackErr);
        setLoadError('Failed to load accounts from any source');
      }
    } finally {
      setLoading(false);
    }
  }, [seriesFilter]);


  // Reload when series filter changes
  useEffect(() => {
    const seriesKey = seriesFilter.length > 0 ? seriesFilter.sort().join(',') : 'all';
    if (seriesKey !== loadedSeries) {
      loadAccounts();
    }
  }, [seriesFilter, loadAccounts, loadedSeries]);

  // Initial load
  useEffect(() => { loadAccounts(); }, []);

  // ─── Add manual entries for selected IDs not in loaded accounts ──
  const allAccounts = useMemo(() => {
    const existingIds = new Set(accounts.map(a => a.account_id));
    const manualAccounts: AccountItem[] = [];
    for (const id of selectedAccountIds) {
      if (!existingIds.has(id)) {
        manualAccounts.push({
          account_id: id,
          account_name: '(manually entered)',
          billed_status: null,
          source: 'manual',
        });
      }
    }
    return [...accounts, ...manualAccounts];
  }, [accounts, selectedAccountIds]);

  // ─── Filtered Accounts ──────────────────────────────────────────
  const filteredAccounts = useMemo(() => {
    let result = allAccounts;
    // Filter by search
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      result = result.filter(a =>
        a.account_id.toLowerCase().includes(s) ||
        (a.account_name || '').toLowerCase().includes(s)
      );
    }
    return result;
  }, [allAccounts, search]);

  // ─── Series Counts ──────────────────────────────────────────────
  const seriesCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of allAccounts) {
      const s = a.account_id.charAt(0);
      counts[s] = (counts[s] || 0) + 1;
    }
    return counts;
  }, [allAccounts]);

  // ─── Source Counts ──────────────────────────────────────────────
  const sourceCounts = useMemo(() => {
    let crm = 0, jobs = 0, manual = 0;
    for (const a of allAccounts) {
      if (a.source === 'crm') crm++;
      else if (a.source === 'jobs') jobs++;
      else manual++;
    }
    return { crm, jobs, manual };
  }, [allAccounts]);

  // ─── Selection Helpers ──────────────────────────────────────────
  const toggleAccount = useCallback((accountId: string) => {
    const newSet = new Set(selectedSet);
    if (newSet.has(accountId)) newSet.delete(accountId);
    else newSet.add(accountId);
    onSelectionChange(Array.from(newSet));
  }, [selectedSet, onSelectionChange]);

  const selectAllVisible = useCallback(() => {
    const newSet = new Set(selectedSet);
    for (const a of filteredAccounts) newSet.add(a.account_id);
    onSelectionChange(Array.from(newSet));
  }, [filteredAccounts, selectedSet, onSelectionChange]);

  const deselectAllVisible = useCallback(() => {
    const idsToRemove = new Set(filteredAccounts.map(a => a.account_id));
    const remaining = Array.from(selectedSet).filter(id => !idsToRemove.has(id));
    onSelectionChange(remaining);
  }, [filteredAccounts, selectedSet, onSelectionChange]);

  const clearAll = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);

  const addManualAccount = useCallback(() => {
    const trimmed = manualInput.trim();
    if (!trimmed) return;
    // Support comma-separated entry
    const ids = trimmed.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    const newSet = new Set(selectedSet);
    for (const id of ids) {
      newSet.add(id);
    }
    onSelectionChange(Array.from(newSet));
    setManualInput('');
  }, [manualInput, selectedSet, onSelectionChange]);

  const selectedInView = useMemo(() =>
    filteredAccounts.filter(a => selectedSet.has(a.account_id)).length,
    [filteredAccounts, selectedSet]
  );

  const allInViewSelected = filteredAccounts.length > 0 && selectedInView === filteredAccounts.length;

  // Don't render if no series selected and no label (unless there are already selected accounts)
  if (seriesFilter.length === 0 && !label && selectedSet.size === 0) return null;

  return (
    <div className={`space-y-1.5 ${compact ? '' : 'mt-2'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Users className="w-3 h-3 text-teal-400" />
          <span className="text-[10px] text-teal-400 uppercase font-semibold">
            {label || `Accounts in ${seriesFilter.join(', ')}-Series`}
          </span>
          {accounts.length > 0 && (
            <Badge className="bg-slate-800/60 text-slate-300 border-slate-600/40 text-[9px] px-1.5 py-0">
              {accounts.length.toLocaleString()} loaded
            </Badge>
          )}
          {selectedSet.size > 0 && (
            <Badge className="bg-teal-900/30 text-teal-400 border-teal-700/40 text-[9px] px-1.5 py-0">
              {selectedSet.size} selected
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">

          <button
            onClick={() => setShowManualEntry(!showManualEntry)}
            className={`text-[9px] px-1.5 py-0.5 rounded border transition-colors flex items-center gap-0.5 ${
              showManualEntry
                ? 'bg-teal-900/20 text-teal-400 border-teal-700/40'
                : 'border-border/40 text-muted-foreground hover:text-foreground hover:border-border'
            }`}
            title="Manually enter account number"
          >
            <Keyboard className="w-2.5 h-2.5" /> Manual
          </button>
          {selectedSet.size > 0 && (
            <button
              onClick={clearAll}
              className="text-[9px] text-red-400 hover:text-red-300 flex items-center gap-0.5 px-1"
            >
              <X className="w-2.5 h-2.5" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Manual Entry */}
      {showManualEntry && (
        <div className="flex gap-1.5 items-center bg-teal-900/10 border border-teal-700/20 rounded px-2 py-1.5">
          <Keyboard className="w-3 h-3 text-teal-400 flex-shrink-0" />
          <Input
            value={manualInput}
            onChange={e => setManualInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addManualAccount(); }}
            placeholder="Enter account # (e.g. 6009) or comma-separated"
            className="dark-input text-xs h-6 flex-1"
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={addManualAccount}
            disabled={!manualInput.trim()}
            className="h-6 text-[10px] px-2 text-teal-400 hover:text-teal-300"
          >
            <Plus className="w-3 h-3 mr-0.5" /> Add
          </Button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search accounts..."
          className="dark-input text-xs h-7 pl-7"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Bulk Actions */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <Button size="sm" variant="ghost" onClick={allInViewSelected ? deselectAllVisible : selectAllVisible}
          className="h-5 text-[9px] px-1.5 text-muted-foreground hover:text-foreground">
          {allInViewSelected ? (
            <><Square className="w-2.5 h-2.5 mr-0.5" /> Deselect All ({filteredAccounts.length})</>
          ) : (
            <><CheckSquare className="w-2.5 h-2.5 mr-0.5" /> Select All ({filteredAccounts.length})</>
          )}
        </Button>
        {seriesFilter.length > 1 && Object.keys(seriesCounts).length > 1 && (
          <>
            {Object.keys(seriesCounts).sort().map(s => (
              <button
                key={s}
                onClick={() => {
                  const seriesAccounts = filteredAccounts.filter(a => a.account_id.charAt(0) === s);
                  const newSet = new Set(selectedSet);
                  for (const a of seriesAccounts) newSet.add(a.account_id);
                  onSelectionChange(Array.from(newSet));
                }}
                className="text-[9px] px-1.5 py-0.5 rounded border border-border/40 text-muted-foreground hover:border-border hover:text-foreground transition-colors"
              >
                +{s} ({seriesCounts[s]})
              </button>
            ))}
          </>
        )}
      </div>

      {/* Account List */}
      <div className={`overflow-y-auto ${maxHeight} border border-border/30 rounded`}>
        {loading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            <span className="text-xs">Loading accounts...</span>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground text-xs space-y-2">
            <Building2 className="w-5 h-5 mx-auto mb-1.5 opacity-30" />
            <p>No accounts found{search ? ' matching search' : ' in this series'}.</p>
            {loadError && (
              <p className="text-[9px] text-amber-400/70">{loadError}</p>
            )}
            <p className="text-[9px] text-muted-foreground/60">
              Use the <span className="text-teal-400">Manual</span> button above to enter account numbers directly.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border/20">
            {filteredAccounts.map(account => {
              const isSelected = selectedSet.has(account.account_id);
              const accountSeries = account.account_id.charAt(0);
              return (
                <div
                  key={account.account_id}
                  className={`flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-secondary/40 transition-colors ${
                    isSelected ? 'bg-teal-900/10' : ''
                  }`}
                  onClick={() => toggleAccount(account.account_id)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleAccount(account.account_id)}
                    className="flex-shrink-0 h-3.5 w-3.5"
                  />
                  <Badge className={`${seriesColor(accountSeries)} text-[8px] px-1 py-0 flex-shrink-0`}>
                    {accountSeries}
                  </Badge>
                  <span className="text-[11px] font-mono text-foreground flex-shrink-0 w-14">
                    {account.account_id}
                  </span>
                  <span className="text-[11px] text-muted-foreground truncate flex-1 min-w-0">
                    {account.account_name || '-'}
                  </span>
                  {account.source === 'manual' && (
                    <Badge className="bg-gray-800/40 text-gray-400 border-gray-700/40 text-[7px] px-1 py-0 flex-shrink-0">
                      Manual
                    </Badge>
                  )}
                  {account.source === 'jobs' && (
                    <Badge className="bg-indigo-900/30 text-indigo-400 border-indigo-700/40 text-[7px] px-1 py-0 flex-shrink-0">
                      Jobs
                    </Badge>
                  )}
                  {account.billed_status === 'Billed' && (
                    <Badge className="bg-green-900/30 text-green-400 border-green-700/40 text-[7px] px-1 py-0 flex-shrink-0">
                      Billed
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected chips */}
      {selectedSet.size > 0 && selectedSet.size <= 15 && (
        <div className="flex flex-wrap gap-1">
          {Array.from(selectedSet).sort().map(id => {
            const acc = allAccounts.find(a => a.account_id === id);
            return (
              <Badge
                key={id}
                className="bg-teal-900/20 text-teal-400 border-teal-700/30 text-[8px] px-1 py-0 cursor-pointer hover:bg-teal-900/40 transition-colors"
                onClick={() => toggleAccount(id)}
              >
                {id}
                {acc?.account_name && acc.account_name !== '(manually entered)' && (
                  <span className="text-teal-400/60 ml-0.5 max-w-[60px] truncate">{acc.account_name}</span>
                )}
                <X className="w-2 h-2 ml-0.5 opacity-60" />
              </Badge>
            );
          })}
        </div>
      )}

      {/* Count info */}
      <div className="text-[9px] text-muted-foreground/50">
        {filteredAccounts.length} accounts in list
        {selectedInView > 0 && <span className="text-teal-400/70 ml-1">({selectedInView} selected in view)</span>}
        {selectedSet.size > 0 && selectedSet.size !== selectedInView && (
          <span className="text-teal-400/50 ml-1">({selectedSet.size} total selected)</span>
        )}
        {allAccounts.length > 0 && (
          <span className="ml-1">
            · {sourceCounts.crm} CRM
            {sourceCounts.jobs > 0 && `, ${sourceCounts.jobs} from jobs`}
            {sourceCounts.manual > 0 && `, ${sourceCounts.manual} manual`}
          </span>
        )}
      </div>
    </div>
  );
};

export default AccountFilterPicker;
